import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Save, Plus, Trash2, UploadCloud, Loader2, Image as ImageIcon } from "lucide-react";
import { AdminShell } from "@/components/admin/AdminShell";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { api, resolveImg } from "@/lib/api";
import { toast } from "sonner";
import { hasAdminSession } from "@/lib/session";

function ImageUpload({ value, onChange, testid, aspect = "aspect-video" }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const upload = async (files) => {
    const file = files?.[0];
    if (!file || !file.type.startsWith("image/")) return;
    setBusy(true);
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(data.url); toast.success("Image uploaded");
    } catch (e) { toast.error(e.response?.data?.detail || "Upload failed"); }
    finally { setBusy(false); }
  };
  return (
    <div>
      <div onClick={() => ref.current?.click()} data-testid={testid}
        className={`relative ${aspect} w-full rounded-lg border-2 border-dashed border-border bg-[#F7F0E6] overflow-hidden cursor-pointer flex items-center justify-center`}>
        <input ref={ref} type="file" accept="image/*" className="hidden" onChange={(e) => { upload(e.target.files); e.target.value = ""; }} />
        {value ? <img src={resolveImg(value)} alt="" className="h-full w-full object-cover" /> : (
          <div className="flex flex-col items-center gap-1 text-muted-foreground text-sm"><ImageIcon className="h-6 w-6" /> Click to upload</div>
        )}
        {busy && <div className="absolute inset-0 bg-white/70 flex items-center justify-center"><Loader2 className="h-5 w-5 animate-spin" /></div>}
      </div>
      <div className="flex items-center gap-2 mt-2">
        <Button type="button" size="sm" variant="secondary" className="gap-1" onClick={() => ref.current?.click()}><UploadCloud className="h-3.5 w-3.5" /> Upload</Button>
        {value && <Button type="button" size="sm" variant="ghost" onClick={() => onChange("")}>Clear</Button>}
      </div>
    </div>
  );
}

export default function Content() {
  const navigate = useNavigate();
  const [home, setHome] = useState(null);
  const [loginPage, setLoginPage] = useState(null);
  const [cats, setCats] = useState([]);
  const [saving, setSaving] = useState(false);
  const [savingLogin, setSavingLogin] = useState(false);
  const [newCat, setNewCat] = useState({ name: "", image: "" });

  const loadCats = () => api.get("/categories").then(({ data }) => setCats(data));
  // Attach a stable React key to each editable list item (server data has no unique id
  // for these embedded arrays, so index-as-key would break Input state on delete/reorder).
  const withKeys = (data) => {
    const tagKeys = (arr) => (arr || []).map((item) => (item._key ? item : { ...item, _key: crypto.randomUUID() }));
    return { ...data, trust_badges: tagKeys(data.trust_badges), collections: tagKeys(data.collections), trust_strip: tagKeys(data.trust_strip) };
  };
  useEffect(() => {
    if (!hasAdminSession()) { navigate("/admin/login"); return; }
    api.get("/admin/homepage").then(({ data }) => setHome(withKeys(data)));
    api.get("/admin/login-page").then(({ data }) => setLoginPage(data));
    loadCats();
    // eslint-disable-next-line
  }, []);

  const saveHome = async () => {
    setSaving(true);
    try { await api.put("/admin/homepage", home); toast.success("Homepage content saved"); }
    catch { toast.error("Save failed"); } finally { setSaving(false); }
  };
  const saveLoginPage = async () => {
    setSavingLogin(true);
    try { await api.put("/admin/login-page", loginPage); toast.success("Login page content saved"); }
    catch { toast.error("Save failed"); } finally { setSavingLogin(false); }
  };
  const setBadge = (i, k, v) => {
    const b = [...(home.trust_badges || [])]; b[i] = { ...b[i], [k]: v }; setHome({ ...home, trust_badges: b });
  };
  const listSet = (key, i, patch) => {
    const a = [...(home[key] || [])]; a[i] = { ...a[i], ...patch }; setHome({ ...home, [key]: a });
  };
  const listAdd = (key, item) => setHome({ ...home, [key]: [...(home[key] || []), { ...item, _key: crypto.randomUUID() }] });
  const listDel = (key, i) => setHome({ ...home, [key]: (home[key] || []).filter((_, x) => x !== i) });

  const saveCat = async (cat) => {
    try { await api.put(`/admin/categories/${cat.slug}`, { name: cat.name, image: cat.image, order: cat.order }); toast.success("Category saved"); loadCats(); }
    catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };
  const deleteCat = async (slug) => {
    try { await api.delete(`/admin/categories/${slug}`); toast.success("Category deleted"); loadCats(); }
    catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };
  const addCat = async () => {
    if (!newCat.name.trim()) return;
    try { await api.post("/admin/categories", newCat); toast.success("Category added"); setNewCat({ name: "", image: "" }); loadCats(); }
    catch (e) { toast.error(e.response?.data?.detail || "Add failed"); }
  };

  if (!home || !loginPage) return <AdminShell title="Content"><div className="py-24 text-center text-muted-foreground">Loading...</div></AdminShell>;

  return (
    <AdminShell title="Content & Categories">
      <Tabs defaultValue="homepage">
        <TabsList>
          <TabsTrigger data-testid="content-tab-homepage" value="homepage">Homepage</TabsTrigger>
          <TabsTrigger data-testid="content-tab-login" value="login">Login Page</TabsTrigger>
          <TabsTrigger data-testid="content-tab-categories" value="categories">Categories</TabsTrigger>
        </TabsList>

        <TabsContent value="homepage" className="mt-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card className="p-6 space-y-3 lg:col-span-2">
              <h3 className="font-medium">Brand Logo</h3>
              <p className="text-xs text-muted-foreground">Upload a logo image to replace the text logo, or edit the text below. Shown in the header and footer.</p>
              <div className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_240px] gap-4 items-start">
                <div><Label className="text-xs">Logo Text (line 1)</Label><Input data-testid="home-logo-text-input" value={home.logo_text || ""} onChange={(e) => setHome({ ...home, logo_text: e.target.value })} className="mt-1" placeholder="MAAHEY" /></div>
                <div><Label className="text-xs">Logo Subtext (line 2)</Label><Input data-testid="home-logo-subtext-input" value={home.logo_subtext || ""} onChange={(e) => setHome({ ...home, logo_subtext: e.target.value })} className="mt-1" placeholder="JEWELS" /></div>
                <div><Label className="text-xs mb-1 block">Logo Image (optional)</Label><ImageUpload testid="home-logo-image-upload" aspect="aspect-[3/1]" value={home.logo_image} onChange={(v) => setHome({ ...home, logo_image: v })} /></div>
              </div>
            </Card>

            <Card className="p-6 space-y-3">
              <h3 className="font-medium">Header, Titles & Footer</h3>
              <div><Label className="text-xs">Top Announcement Strip</Label><Input data-testid="home-announcement-input" value={home.announcement || ""} onChange={(e) => setHome({ ...home, announcement: e.target.value })} className="mt-1" /></div>
              <div><Label className="text-xs">Bestsellers Section Title</Label><Input data-testid="home-featured-title-input" value={home.featured_title || ""} onChange={(e) => setHome({ ...home, featured_title: e.target.value })} className="mt-1" placeholder="Our Bestsellers" /></div>
              <div><Label className="text-xs">Footer Tagline</Label><Textarea value={home.footer_tagline || ""} onChange={(e) => setHome({ ...home, footer_tagline: e.target.value })} rows={2} className="mt-1" /></div>
              <p className="text-xs text-muted-foreground pt-1">Category tiles are managed in the <strong>Categories</strong> tab (image, name, order).</p>
            </Card>

            <Card className="p-6 space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="font-medium">Our Promise</h3>
                <Button type="button" size="sm" variant="secondary" className="gap-1" data-testid="add-promise-button" onClick={() => listAdd("trust_badges", { title: "" })}><Plus className="h-3.5 w-3.5" /> Add</Button>
              </div>
              <p className="text-xs text-muted-foreground">Icons are applied automatically in order (award, shipping, returns, exchange).</p>
              {(home.trust_badges || []).map((b, i) => (
                <div key={b._key || i} className="flex items-center gap-2 border border-border rounded-lg p-2">
                  <Input data-testid={`promise-input-${i}`} value={b.title || ""} onChange={(e) => setBadge(i, "title", e.target.value)} placeholder="Promise text" />
                  <Button type="button" size="icon" variant="ghost" className="shrink-0 text-destructive" data-testid={`promise-remove-${i}`} onClick={() => listDel("trust_badges", i)}><Trash2 className="h-4 w-4" /></Button>
                </div>
              ))}
            </Card>

            <Card className="p-6 space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="font-medium">Shop By Collection</h3>
                <Button type="button" size="sm" variant="secondary" className="gap-1" data-testid="add-collection-button" onClick={() => listAdd("collections", { title: "", image: "", dark: false, link: "/products" })}><Plus className="h-3.5 w-3.5" /> Add</Button>
              </div>
              {(home.collections || []).map((col, i) => (
                <div key={col._key || i} className="border border-border rounded-lg p-3 space-y-2">
                  <div className="flex items-start gap-3">
                    <div className="w-28 shrink-0"><ImageUpload testid={`collection-image-${i}`} aspect="aspect-[3/4]" value={col.image} onChange={(v) => listSet("collections", i, { image: v })} /></div>
                    <div className="flex-1 space-y-2">
                      <div><Label className="text-xs">Title</Label><Input data-testid={`collection-title-${i}`} value={col.title || ""} onChange={(e) => listSet("collections", i, { title: e.target.value })} className="mt-1" /></div>
                      <div><Label className="text-xs">Link (page)</Label><Input value={col.link || ""} onChange={(e) => listSet("collections", i, { link: e.target.value })} className="mt-1" placeholder="/products?category=Rings" /></div>
                      <div className="flex items-center justify-between pt-1">
                        <Label className="text-xs">Dark overlay (light image text)</Label>
                        <Switch checked={!!col.dark} onCheckedChange={(v) => listSet("collections", i, { dark: v })} data-testid={`collection-dark-${i}`} />
                      </div>
                    </div>
                    <Button type="button" size="icon" variant="ghost" className="shrink-0 text-destructive" data-testid={`collection-remove-${i}`} onClick={() => listDel("collections", i)}><Trash2 className="h-4 w-4" /></Button>
                  </div>
                </div>
              ))}
            </Card>

            <Card className="p-6 space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="font-medium">Bottom Trust Strip</h3>
                <Button type="button" size="sm" variant="secondary" className="gap-1" data-testid="add-trust-button" onClick={() => listAdd("trust_strip", { label: "" })}><Plus className="h-3.5 w-3.5" /> Add</Button>
              </div>
              <p className="text-xs text-muted-foreground">Icons are applied automatically in order.</p>
              {(home.trust_strip || []).map((t, i) => (
                <div key={t._key || i} className="flex items-center gap-2 border border-border rounded-lg p-2">
                  <Input data-testid={`trust-input-${i}`} value={t.label || ""} onChange={(e) => listSet("trust_strip", i, { label: e.target.value })} placeholder="Trust label" />
                  <Button type="button" size="icon" variant="ghost" className="shrink-0 text-destructive" data-testid={`trust-remove-${i}`} onClick={() => listDel("trust_strip", i)}><Trash2 className="h-4 w-4" /></Button>
                </div>
              ))}
            </Card>
            <div className="lg:col-span-2">
              <Button data-testid="home-content-save" onClick={saveHome} disabled={saving} className="gap-2 h-12 w-full sm:w-auto"><Save className="h-4 w-4" /> {saving ? "Saving..." : "Save Homepage Content"}</Button>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="login" className="mt-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card className="p-6 space-y-3">
              <h3 className="font-medium">Retailer Login Screen</h3>
              <div><Label className="text-xs">Eyebrow (small label)</Label><Input data-testid="login-eyebrow-input" value={loginPage.eyebrow || ""} onChange={(e) => setLoginPage({ ...loginPage, eyebrow: e.target.value })} className="mt-1" /></div>
              <div><Label className="text-xs">Headline</Label><Textarea data-testid="login-headline-input" value={loginPage.brand_line || ""} onChange={(e) => setLoginPage({ ...loginPage, brand_line: e.target.value })} rows={2} className="mt-1" /></div>
              <div><Label className="text-xs">Subtitle</Label><Textarea data-testid="login-subtitle-input" value={loginPage.subtitle || ""} onChange={(e) => setLoginPage({ ...loginPage, subtitle: e.target.value })} rows={2} className="mt-1" /></div>
              <div><Label className="text-xs">Footer Line</Label><Input value={loginPage.footer_line || ""} onChange={(e) => setLoginPage({ ...loginPage, footer_line: e.target.value })} className="mt-1" /></div>
            </Card>
            <Card className="p-6 space-y-3">
              <h3 className="font-medium">Hero Image</h3>
              <ImageUpload testid="login-hero-image-upload" value={loginPage.hero_image} onChange={(v) => setLoginPage({ ...loginPage, hero_image: v })} />
            </Card>
            <div className="lg:col-span-2">
              <Button data-testid="login-content-save" onClick={saveLoginPage} disabled={savingLogin} className="gap-2 h-12 w-full sm:w-auto"><Save className="h-4 w-4" /> {savingLogin ? "Saving..." : "Save Login Page Content"}</Button>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="categories" className="mt-4">
          <Card className="p-6 mb-4">
            <h3 className="font-medium mb-3">Add New Category</h3>
            <div className="grid grid-cols-1 sm:grid-cols-[1fr_240px_auto] gap-3 items-end">
              <div><Label className="text-xs">Name</Label><Input data-testid="new-category-name" value={newCat.name} onChange={(e) => setNewCat({ ...newCat, name: e.target.value })} className="mt-1" placeholder="e.g. Nose Pins" /></div>
              <div><Label className="text-xs mb-1 block">Image</Label><ImageUpload testid="new-category-image" aspect="aspect-square" value={newCat.image} onChange={(v) => setNewCat({ ...newCat, image: v })} /></div>
              <Button data-testid="add-category-button" onClick={addCat} className="gap-1 h-10"><Plus className="h-4 w-4" /> Add</Button>
            </div>
          </Card>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="admin-categories-list">
            {cats.map((cat) => (
              <Card key={cat.slug} className="p-4">
                <ImageUpload aspect="aspect-square" value={cat.image} onChange={(v) => setCats(cats.map((x) => x.slug === cat.slug ? { ...x, image: v } : x))} />
                <div className="mt-3"><Label className="text-xs">Name</Label><Input value={cat.name} onChange={(e) => setCats(cats.map((x) => x.slug === cat.slug ? { ...x, name: e.target.value } : x))} className="mt-1" /></div>
                <div className="mt-2"><Label className="text-xs">Display Order</Label><Input type="number" value={cat.order} onChange={(e) => setCats(cats.map((x) => x.slug === cat.slug ? { ...x, order: Number(e.target.value) } : x))} className="mt-1 w-24" /></div>
                <div className="flex gap-2 mt-3">
                  <Button data-testid="save-category-button" size="sm" onClick={() => saveCat(cat)} className="gap-1"><Save className="h-3.5 w-3.5" /> Save</Button>
                  <Button data-testid="delete-category-button" size="sm" variant="ghost" onClick={() => deleteCat(cat.slug)} className="gap-1 text-destructive"><Trash2 className="h-3.5 w-3.5" /> Delete</Button>
                </div>
              </Card>
            ))}
          </div>
        </TabsContent>
      </Tabs>
    </AdminShell>
  );
}
